/**
 * useCapabilityHydration.ts — MER Algo Footprints — Phase 6 foundation (P1-Q4 / G-CLIENT-1)
 *
 * One-shot advisory capability hydration for position 8. Realizes D-GCLIENT1-3
 * (ADR section 48): `GET /api/capabilities` is fetched exactly once per
 * authenticated application-shell lifecycle, using the same lifecycle concept
 * as `useUserPrefsHydration`.
 *
 * There is no interval polling, no render-triggered repeat, no market-data,
 * symbol, horizon, or canonical-timeframe trigger, and no automatic retry. A
 * legitimate new authenticated lifecycle after logout is permitted exactly one
 * new fetch; a rerender within the same lifecycle is not.
 *
 * This hook owns the fetch lifecycle only. It renders nothing, exposes no
 * advisory UI, performs no canonical selection, and never calls the G-API-4B
 * shadow ingress — position 9 remains the sole future sender.
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useStore } from '../store';
import { validateCapabilityResponse } from '../shared/capabilities/gClient1Capability';

const CAPABILITIES_ENDPOINT = '/api/capabilities';

export function useCapabilityHydration(): void {
  const { status } = useAuth();
  const applySuccess = useStore(s => s.applyCapabilitySuccess);
  const applyFailure = useStore(s => s.applyCapabilityFailure);

  // Tracks the authenticated lifecycle already hydrated. Rerenders inside one
  // authenticated lifecycle do not refetch; a genuine unauthenticated →
  // authenticated transition arms exactly one new fetch. This does not rely on
  // the absence of StrictMode for its one-shot guarantee.
  const hydratedLifecycle = useRef(false);

  useEffect(() => {
    if (status !== 'authenticated') {
      // Leaving the authenticated lifecycle re-arms a single future hydration.
      hydratedLifecycle.current = false;
      return undefined;
    }

    if (hydratedLifecycle.current) return undefined;
    hydratedLifecycle.current = true;

    // Guards a late completion from being applied after the authenticated
    // lifecycle has ended or the shell has unmounted. It is a safety discard
    // only — there is no retry, queue, or request-ordering architecture.
    let discarded = false;

    (async () => {
      let body: unknown;
      try {
        const response = await fetch(CAPABILITIES_ENDPOINT, { credentials: 'include' });
        if (!response.ok) {
          if (!discarded) {
            applyFailure({ kind: 'TRANSPORT_FAILURE', detail: 'capability request was not ok' });
          }
          return;
        }
        body = await response.json();
      } catch (_e) {
        // Network, abort, and JSON-parse failures are all transport-channel
        // events. They never become a capability reason code.
        if (!discarded) {
          applyFailure({ kind: 'TRANSPORT_FAILURE', detail: 'capability request failed' });
        }
        return;
      }

      if (discarded) return;

      const validation = validateCapabilityResponse(body);
      if (validation.ok) {
        applySuccess(validation.overlay);
      } else {
        applyFailure(validation.diagnostic);
      }
    })();

    return () => { discarded = true; };
  }, [status, applySuccess, applyFailure]);
}
